import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { AuthRolesService } from '../../../core/guard/authorization/authorization-guard';
import { getVisibleHelpTopics } from '@features/help/data-access/help-content';

interface MenuSidebarItem {
	label: string;
	icon: string;
	route: string;
}

interface MenuSidebarGroup {
	label: string;
	items: MenuSidebarItem[];
	collapsible?: boolean;
}

@Component({
	selector: 'app-menu-sidebar',
	standalone: true,
	imports: [RouterLink, RouterLinkActive],
	templateUrl: './menu-sidebar.html',
	styleUrl: './menu-sidebar.css',
})
export class MenuSidebar implements OnInit, OnDestroy {
	@Input() isMobileViewMenu: boolean = false;

	groups: MenuSidebarGroup[] = [];
	private expandedGroups = new Set<string>();
	private routerSubscription?: Subscription;

	constructor(
		private router: Router,
		private authRolesService: AuthRolesService,
	) {}

	ngOnInit() {
		const type = this.authRolesService.getCurrentUserType();
		if (!type) {
			this.router.navigate(['/login']);
			throw new Error(
				'Tipo de usuário não definido. Redirecionando para login.',
			);
		}

		this.groups = [
			{
				label: 'Serviços',
				items: [
					{
						label: 'Predições',
						icon: 'pi pi-check-square',
						route: '/main/results',
					},
					{
						label: 'Mapeamento',
						icon: 'pi pi-map',
						route: '/main/statusFarm',
					},
				],
			},
			{
				label: 'Configurações',
				items: [
					{
						label: 'Entrada/saída',
						icon: 'pi pi-calendar',
						route: '/main/entrada-saida',
					},
					{
						label: 'Morte/Medicado',
						icon: 'pi pi-heart',
						route: '/main/mortalidade',
					},
					{
						label: 'Peso médio',
						icon: 'pi pi-chart-line',
						route: '/main/peso-medio',
					},
				],
			},
			...(this.canSeeConfig(type)
				? [
						{
							label: 'ConfigADM',
							items: [
								{
									label: 'Fazendas',
									icon: 'pi pi-briefcase',
									route: '/main/farm',
								},
								{
									label: 'Usuários',
									icon: 'pi pi-users',
									route: '/main/customer',
								},
								{
									label: 'Baias',
									icon: 'pi pi-map-marker',
									route: '/main/feedlot',
								},
								{
									label: 'Ortofotos',
									icon: 'pi pi-image',
									route: '/main/ortofoto',
								},
							],
						},
					]
				: []),
			...(this.canSeeConfigDev(type)
				? [
						{
							label: 'Dev',
							collapsible: true,
							items: [
								{
									label: 'config dev',
									icon: 'pi pi-cog',
									route: '/main/dev/config',
								},
								{
									label: 'Estatísticas do banco',
									icon: 'pi pi-chart-bar',
									route: '/main/dev/estatisticas',
								},
							],
						},
					]
				: []),
			{
				label: 'Ajuda',
				collapsible: true,
				items: getVisibleHelpTopics(type).map((topic) => ({
					label: topic.title,
					icon: topic.icon,
					route: `/main/help/${topic.id}`,
				})),
			},
		];

		this.expandActiveGroup(this.router.url);
		this.routerSubscription = this.router.events
			.pipe(filter((event) => event instanceof NavigationEnd))
			.subscribe((event) => {
				this.expandActiveGroup((event as NavigationEnd).urlAfterRedirects);
			});
	}

	ngOnDestroy() {
		this.routerSubscription?.unsubscribe();
	}

	private expandActiveGroup(url: string) {
		const group = this.groups.find((g) =>
			g.items.some((item) => url.startsWith(item.route)),
		);
		if (group?.collapsible) this.expandedGroups.add(group.label);
	}

	toggleGroup(label: string) {
		if (this.expandedGroups.has(label)) {
			this.expandedGroups.delete(label);
		} else {
			this.expandedGroups.add(label);
		}
	}

	isGroupExpanded(label: string): boolean {
		return this.expandedGroups.has(label);
	}

	private canSeeConfig(role: string): boolean {
		return role === 'admin' || role === 'dev';
	}

	private canSeeConfigDev(role: string): boolean {
		return role === 'dev';
	}
}
